import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Button } from "@mui/material";
import { MRT_RowSelectionState } from "material-react-table";

import { TrashIcon } from "assets";
import { Bill, deleteUtilityBill } from "api";
import {
  useDocumentBills,
  useGetConfigParameter,
  useNotify,
  useUtilityBillById,
} from "hooks";
import { useSaveCurrentUrl, useUserRole } from "recoils";
import {
  getFromLocalStorage,
  setToLocalStorage,
  isFeatureEnabled,
} from "utils";
import { QUERY_KEYS } from "consts";
import {
  CustomMaterialTable,
  DeleteDialog,
  Label12Regular,
  Label16SemiBold,
  Loading,
  useDialogControls,
} from "components";

import { UTILITY_BILL_PAGE } from "pages/authenticated/documents/document-summary-v2/route-consts";

import { DOCUMENT_UTILITY_BILLS_COLUMNS } from "./columns";
import { DOCUMENT_UPLOAD_PAGE, ADD_DOCUMENT_BILL_PAGE } from "../../consts";

import { DocumentExpandedRowProps } from "./types";

import { UtilityBillsBulkStatusBar } from "./components";

export const DocumentExpandedRow: React.FunctionComponent<
  DocumentExpandedRowProps
> = ({ row }) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const deleteDialogControls = useDialogControls();
  const saveCurrentUrl = useSaveCurrentUrl();
  const navigate = useNavigate();
  const { mutateAsync: deleteUB } = useMutation(deleteUtilityBill);
  const dataEntriesFromStorage = getFromLocalStorage("dataEntries") || "";
  const dataEntries = dataEntriesFromStorage
    ? JSON.parse(dataEntriesFromStorage)?.original?.bills
    : "";

  const documentId = window.sessionStorage.getItem("summaryDocumentID");

  const document = getFromLocalStorage("document") || "";
  const documentData = (document && JSON.parse(document)) || "";
  const addEntriesDelete = getFromLocalStorage("billEntriesIdSelected");
  const clickedDataEntryId = getFromLocalStorage("dataEntryIdSelected");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const rowSelectionLength = Object.keys(rowSelection).length;
  const selectedBillIds = Object.keys(rowSelection);
  const { billId } = useParams<{ [UTILITY_BILL_PAGE.KEY]: string }>();
  const { data: utilityBill } = useUtilityBillById(billId || "");
  const documentGUID = utilityBill?.documentGuid;
  const { data, isLoading } = useDocumentBills(
    documentGUID || documentId || "",
  );
  const userRole = useUserRole();
  const { data: configParameter } = useGetConfigParameter();
  const showReviewed = isFeatureEnabled(
    configParameter,
    "second_layer_approval_feature",
  );
  const showNotApplicable = isFeatureEnabled(
    configParameter,
    "unusable_status_enabled",
  );
  const currentURL = new URL(window.location.href).pathname;

  let billsLength: number;
  if (data && data.length > 0) {
    const firstObject = data[0];
    if (firstObject && firstObject.bills) {
      billsLength = firstObject.bills.length;
    }
  }

  const onDeleteClick = () => {
    localStorage.removeItem("document");
    const selectedIds = Object.keys(rowSelection);
    const idsToDelete =
      selectedIds.length >= 1
        ? selectedIds
        : addEntriesDelete
        ? [addEntriesDelete]
        : [];
    Promise.allSettled(idsToDelete.map((id) => deleteUB(id))).then(
      (results) => {
        const successful = results.filter(
          (result) => result.status === "fulfilled",
        );
        const failed = results.filter((result) => result.status === "rejected");

        if (successful.length > 0) {
          notify.success(
            `${successful.length} utility bill(s) deleted successfully!`,
          );
        }

        if (failed.length > 0) {
          failed.forEach((result, index) => {
            const id = idsToDelete[index];
            notify.error(`Failed to delete utility bill: ${id}`);
          });
        }

        localStorage.removeItem("billEntriesIdSelected");
        deleteDialogControls.close();
        setRowSelection({} as MRT_RowSelectionState);
        if (billsLength <= 1) {
          navigate(DOCUMENT_UPLOAD_PAGE.ABSOLUTE_PATH);
        }
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_BILLS]);
      },
    );
  };

  const handleRowClick = async (rowData: any) => {
    const currentId = rowData?.id || rowData?.original?.id;

    if (currentId === undefined || currentId === null) {
      notify.error(
        "Manual data entries are disabled for system-generated bills.",
      );
      return;
    }

    saveCurrentUrl();
    navigate(ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(currentId));
  };

  const handleDataEntryClick = (dataEntry: any) => {
    const dataEntryToStore = dataEntry?.row?.id || dataEntry?.row?.original?.id;

    if (dataEntryToStore) {
      setToLocalStorage("dataEntryIdSelected", dataEntryToStore);

      navigate(`${UTILITY_BILL_PAGE.ABSOLUTE_PATH(dataEntryToStore)}`);
    }
  };

  const getMuiTableBodyRowProps = (dataEntry: any) => ({
    onClick: () => handleDataEntryClick(dataEntry),
    sx: {
      backgroundColor:
        String(dataEntry?.row?.original?.id) === String(clickedDataEntryId)
          ? "#DCDCDC"
          : "",
      cursor: "pointer",
    },
  });

  useEffect(() => {
    if (documentData?.filename !== undefined) {
      if (
        row &&
        row.original &&
        row.original.fileName !== documentData.filename
      ) {
        localStorage.removeItem("document");
      }
    }
  }, [currentURL, documentData?.filename, row, row.original]);

  return (
    <Box
      sx={(theme) => ({
        padding: 3,
        border: `1px solid ${theme.palette.background.default}`,
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.25)",
        marginBottom: "2px",
      })}
    >
      <Box
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Label16SemiBold mb={0.5}>
            {document === ""
              ? row && row.original && row.original.fileName
              : documentData?.filename}
          </Label16SemiBold>
          <Label12Regular>Billed service(s)</Label12Regular>
        </Box>
        {(!(dataEntries?.length === 0) || documentId !== "") && (
          <Box>
            <Button
              startIcon={<TrashIcon />}
              variant="outlined"
              disabled={rowSelectionLength === 0 || userRole.isAuditor}
              onClick={deleteDialogControls.open}
            >
              Delete
            </Button>
            <Button
              sx={{ ml: 2 }}
              variant="outlined"
              disabled={userRole.isAuditor}
              onClick={() => handleRowClick(row)}
            >
              Add data entry
            </Button>
          </Box>
        )}
      </Box>
      {!isLoading && rowSelectionLength > 0 && (
        <UtilityBillsBulkStatusBar
          selectedBillIds={selectedBillIds}
          selectedCount={rowSelectionLength}
          disabled={userRole.isAuditor}
          onClear={() => setRowSelection({} as MRT_RowSelectionState)}
          showReviewed={showReviewed}
          showNotApplicable={showNotApplicable}
        />
      )}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            height: "300px",
            position: "relative",
            padding: "24px 41px",
          }}
        >
          <Loading />
        </Box>
      ) : (
        <CustomMaterialTable
          data={data?.[0]?.bills ?? []}
          columns={DOCUMENT_UTILITY_BILLS_COLUMNS}
          enablePagination={false}
          enableRowSelection
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection }}
          getRowId={(bill: Bill) => bill.id}
          muiTableBodyRowProps={(billRow: any) =>
            getMuiTableBodyRowProps(billRow)
          }
          containerSx={{
            maxHeight: "calc(100dvh - 320px)",
            overflowY: "auto",
            "& tbody tr td": {
              padding: "12px 16px !important",
              whiteSpace: "nowrap",
            },
            "& tbody tr td:nth-last-child(2)": {
              whiteSpace: "normal",
              wordWrap: "break-word",
              wordBreak: "break-word",
              maxWidth: "220px",
            },
            "& thead tr th:nth-child(1)": {
              padding: "6px 16px",
            },
            "& thead tr th": {
              height: "fit-content",
              alignItems: "center",
              pt: "10px",
              pb: "9px",
            },
          }}
        />
      )}
      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={onDeleteClick}
        selectedCount={rowSelectionLength ?? 0}
        target="bill"
        title={`Delete this data entry in document: ${
          document === ""
            ? row && row.original && row.original.fileName
            : documentData.filename
        }`}
      />
    </Box>
  );
};
